/**
 * Investigate — timeline, hunts, replay jobs, persistence drift. Tabbed.
 *
 * URL contract:
 *   ?tab=timeline|hunts|replay|persistence
 *   ?q=<dsl> ?since=1h|24h|7d|30d ?hunt=<id> ?job=<id>     (hunts/replay)
 *   ?deviceId= ?checkType= ?status=drift,new,missing       (persistence)
 */
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Activity, FastForward, GitBranch, Monitor, Target, Terminal } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SeverityBadge } from "@/components/security/SeverityBadge";
import { OwnerPill } from "@/components/owner-pill";
import { useEdrAlerts } from "@/hooks/security/useEdrData";
import { useHunts, useReplayJobs } from "@/hooks/security/useHunts";
import { usePersistenceSnapshots } from "@/hooks/security/usePersistence";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

import { HuntsTab, type WindowKey } from "@/components/security/hunts/HuntsTab";
import { ReplayJobsTab } from "@/components/security/hunts/ReplayJobsTab";
import { PersistenceTab } from "@/components/security/persistence/PersistenceTab";
import { RtrConsoleTab } from "@/components/security/rtr/RtrConsoleTab";

type Tab = "timeline" | "hunts" | "replay" | "persistence" | "rtr";
const TABS: Tab[] = ["timeline", "hunts", "replay", "persistence", "rtr"];

export default function InvestigatePage() {
  return (
    <ConnectorGate connectorId="edr">
      <InvestigateInner />
    </ConnectorGate>
  );
}

function InvestigateInner() {
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.includes(sp.get("tab") as Tab) ? sp.get("tab") : "timeline") as Tab;

  const alertsQ = useEdrAlerts({ limit: 200 });
  const huntsQ = useHunts();
  const replayQ = useReplayJobs(0);
  const snapsQ = usePersistenceSnapshots();

  const setTab = (next: Tab) => {
    const nextSp = new URLSearchParams(sp);
    nextSp.set("tab", next);
    setSp(nextSp, { replace: true });
  };

  // Generic helper used by Hunts to update one or more URL params atomically.
  const patchParams = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null || v === "") next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );

  // Drift summary chip for the Persistence tab label.
  const driftCount = useMemo(() => {
    const list = snapsQ.data ?? [];
    return list.reduce(
      (acc, s) => acc + (s.summary?.new ?? 0) + (s.summary?.drifted ?? 0) + (s.summary?.missing ?? 0),
      0,
    );
  }, [snapsQ.data]);

  const huntsCount = huntsQ.data?.length ?? 0;
  const jobsCount = replayQ.data?.length ?? 0;

  return (
    <SurfaceShell
      overline="Security · Investigate"
      title="Trace what happened"
      summary="Walk the timeline, hunt with the DSL, replay against history, or audit persistence drift."
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="w-full">
        <TabsList>
          <TabsTrigger value="timeline">
            <Activity className="h-3.5 w-3.5 mr-1.5" /> Timeline
          </TabsTrigger>
          <TabsTrigger value="hunts">
            <Target className="h-3.5 w-3.5 mr-1.5" /> Hunts ({huntsCount})
          </TabsTrigger>
          <TabsTrigger value="replay">
            <FastForward className="h-3.5 w-3.5 mr-1.5" /> Replay jobs ({jobsCount})
          </TabsTrigger>
          <TabsTrigger value="persistence">
            <GitBranch className="h-3.5 w-3.5 mr-1.5" /> Persistence{" "}
            {driftCount > 0 && <Badge variant="default" className="ml-1.5 h-4 text-[10px]">{driftCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="rtr">
            <Terminal className="h-3.5 w-3.5 mr-1.5" /> RTR Console
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-4">
          <TimelineTab
            query={alertsQ}
            alerts={alertsQ.data ?? []}
          />
        </TabsContent>

        <TabsContent value="hunts" className="mt-4">
          <HuntsTab
            initialQuery={sp.get("q") ?? ""}
            initialWindow={(sp.get("since") as WindowKey) ?? "24h"}
            initialHuntId={sp.get("hunt") ?? undefined}
            onChange={(p) =>
              patchParams({
                q: p.q,
                since: p.since,
                hunt: p.hunt === null ? null : p.hunt,
              })
            }
            onReplayQueued={(jobId) => {
              patchParams({ tab: "replay", job: jobId });
              setTab("replay");
            }}
          />
        </TabsContent>

        <TabsContent value="replay" className="mt-4">
          <ReplayJobsTab
            selectedJobId={sp.get("job") ?? undefined}
            onSelectJob={(id) => patchParams({ job: id })}
          />
        </TabsContent>

        <TabsContent value="persistence" className="mt-4">
          <PersistenceTab
            filters={{
              deviceId: sp.get("deviceId") ?? undefined,
              checkType: sp.get("checkType") ?? undefined,
              status: sp.get("status") ?? undefined,
            }}
            onFiltersChange={(next) => patchParams(next)}
          />
        </TabsContent>

        <TabsContent value="rtr" className="mt-4">
          <RtrConsoleTab />
        </TabsContent>
      </Tabs>
    </SurfaceShell>
  );
}

/* ────────────────────── Timeline tab (unchanged) ────────────────────── */

type QueryLike = { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };

type SortKey = "ts" | "severity" | "ruleName" | "family" | "process" | "mitre" | "deviceName";
type SortDir = "asc" | "desc";

const SEVERITY_ORDER: Record<string, number> = {
  critical: 5, high: 4, medium: 3, low: 2, info: 1,
};

function familyLabel(f: string): string {
  return f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function shortenPath(p: string, max = 48): string {
  if (!p || p.length <= max) return p;
  const parts = p.split("/");
  if (parts.length <= 3) return `…${p.slice(-(max - 1))}`;
  return `${parts.slice(0, 2).join("/")}/…/${parts.slice(-2).join("/")}`;
}

function TimelineTab({ query, alerts }: { query: QueryLike; alerts: any[] }) {
  const [q, setQ] = useState("");
  const [severity, setSeverity] = useState<string>("all");
  const [family, setFamily] = useState<string>("all");
  const [device, setDevice] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("ts");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const devices = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of alerts) m.set(a.deviceId, a.deviceName);
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [alerts]);

  const families = useMemo(
    () => Array.from(new Set(alerts.map((a) => a.family))).sort(),
    [alerts],
  );

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    return alerts.filter((a) => {
      if (severity !== "all" && a.severity !== severity) return false;
      if (family !== "all" && a.family !== family) return false;
      if (device !== "all" && a.deviceId !== device) return false;
      if (!ql) return true;
      return `${a.ruleName} ${a.deviceName} ${a.process} ${a.mitre} ${a.summary ?? ""}`
        .toLowerCase()
        .includes(ql);
    });
  }, [alerts, q, severity, family, device]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sortKey === "ts") {
        av = new Date(a.ts).getTime(); bv = new Date(b.ts).getTime();
      } else if (sortKey === "severity") {
        av = SEVERITY_ORDER[a.severity] ?? 0; bv = SEVERITY_ORDER[b.severity] ?? 0;
      } else {
        av = String((a as any)[sortKey] ?? "").toLowerCase();
        bv = String((b as any)[sortKey] ?? "").toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "ts" || k === "severity" ? "desc" : "asc"); }
  };

  const singleDevice = devices.length === 1 ? devices[0] : null;
  const firstAlert = alerts[0];

  return (
    <Panel
      title={`Event timeline · ${sorted.length} of ${alerts.length} events`}
      loading={query.isLoading}
      isError={query.isError}
      error={query.error}
      onRetry={() => query.refetch()}
      empty={!query.isLoading && !query.isError && sorted.length === 0}
      emptyTitle={alerts.length === 0 ? "Timeline is quiet" : "No matching events"}
      emptyDescription={
        alerts.length === 0
          ? "No alerts have been recorded yet."
          : "Try clearing the filters."
      }
      skeleton="table"
    >
      {alerts.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
          <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
          {singleDevice ? (
            <>
              <span className="text-muted-foreground">All events from device</span>
              <span className="font-mono font-medium text-foreground">{singleDevice.name}</span>
              {firstAlert?.consoleUser && (
                <OwnerPill consoleUser={firstAlert.consoleUser} deviceId={singleDevice.id} size="sm" />
              )}
            </>
          ) : (
            <span className="text-muted-foreground">
              Spanning <span className="font-medium text-foreground">{devices.length}</span> devices
            </span>
          )}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by rule, process, MITRE…"
          className="h-8 w-[260px] text-xs"
        />
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
        <Select value={family} onValueChange={setFamily}>
          <SelectTrigger className="h-8 w-[160px] text-xs"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {families.map((f) => (
              <SelectItem key={f} value={f}>{familyLabel(f)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!singleDevice && devices.length > 1 && (
          <Select value={device} onValueChange={setDevice}>
            <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue placeholder="Device" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All devices</SelectItem>
              {devices.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="-mx-4 -my-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <SortHeader label="Time" k="ts" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} className="w-[120px]" />
              <SortHeader label="Severity" k="severity" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} className="w-[100px]" />
              <SortHeader label="Category" k="family" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} className="w-[140px]" />
              <SortHeader label="Rule" k="ruleName" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Process" k="process" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="MITRE" k="mitre" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} className="w-[100px]" />
              {!singleDevice && (
                <SortHeader label="Device" k="deviceName" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} className="w-[140px]" />
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.slice(0, 200).map((a) => (
              <TableRow key={a.id}>
                <TableCell className="text-xs text-muted-foreground tabular-nums" title={format(new Date(a.ts), "PPpp")}>
                  {formatDistanceToNow(new Date(a.ts), { addSuffix: true })}
                </TableCell>
                <TableCell><SeverityBadge severity={a.severity} /></TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px] font-normal">{familyLabel(a.family)}</Badge>
                </TableCell>
                <TableCell className="text-xs font-medium text-foreground">{a.ruleName}</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground" title={a.process}>
                  {shortenPath(a.process)}
                </TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{a.mitre}</TableCell>
                {!singleDevice && (
                  <TableCell className="text-xs font-mono text-muted-foreground">{a.deviceName}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
}

function SortHeader({
  label, k, sortKey, sortDir, onClick, className,
}: {
  label: string; k: SortKey; sortKey: SortKey; sortDir: SortDir;
  onClick: (k: SortKey) => void; className?: string;
}) {
  const active = sortKey === k;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        onClick={() => onClick(k)}
        className={cn(
          "inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider hover:text-foreground",
          active ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
        <Icon className="h-3 w-3" />
      </button>
    </TableHead>
  );
}
